import config from "@/config";
import errors from "@/errors-constants";
import useAuthentication from "@/hooks/use-authentication";
import { markPublicationInProgress } from "@/hooks/publicationStatus.store";
import useToast from "@/hooks/useToastNotification";
import { useAxios } from "@vueuse/integrations/useAxios";
import axios, { AxiosError } from "axios";
import axiosRetry from "axios-retry";

const isUnAuthenticatedError = (error: AxiosError) => error.response?.status === 401;

const PUBLICATION_IN_PROGRESS_REASON = "error.configuration.Application.PublicationInProgress";

/**
 * While a publication holds the app lock, the manager API rejects every mutation with
 * this 409. Surface it as a toast and reflect the lock in the shared publication
 * status immediately — then rethrow, so callers' own error handling still runs.
 */
const notifyPublicationInProgress = (error: AxiosError) => {
  const data = error.response?.data as
    | { reason?: string; details?: { startedBy?: string | null; startedAt?: string | null } }
    | undefined;

  if (error.response?.status === 409 && data?.reason === PUBLICATION_IN_PROGRESS_REASON) {
    const appId = /(?:^|\/)app\/([^/]+)\//.exec(error.config?.url ?? "")?.[1];
    if (appId) {
      markPublicationInProgress(appId, data.details ?? {});
    }
    useToast().toastWarning({
      summary: "Publication in progress",
      detail: errors[PUBLICATION_IN_PROGRESS_REASON].message,
      life: 6000,
    });
  }

  return Promise.reject(error);
};

export default function useBackendClient() {
  const { accessToken, refreshAccessToken, logout, login } = useAuthentication();

  const client = axios.create({
    baseURL: config.apiUrl,
    headers: {
      authorization: `Bearer ${accessToken.value}`,
    },
  });

  client.interceptors.response.use(undefined, notifyPublicationInProgress);

  axiosRetry(client, {
    retries: 3,
    retryCondition: (error) => isUnAuthenticatedError(error),
    async onRetry(retryCount, _, requestConfig) {
      if (retryCount >= 3) {
        await logout();
        await login(window.location.pathname);
      }

      if (requestConfig.headers) {
        const token = await refreshAccessToken();
        requestConfig.headers.Authorization = `Bearer ${token}`;
      }
    },
  });

  return {
    useAxios,
    client,
  };
}
